import {Button, Chip, IconButton, Stack, Typography} from "@mui/material";
import type {GridColDef} from "@mui/x-data-grid-premium";
import EditIcon from '~/icons/svg/EditIcon.svg?react'
import BuildingIcon from '~/icons/svg/BuildingIcon.svg?react'
import StoreIcon from '~/icons/svg/StoreIcon.svg?react'
import {Tag} from "~/components/tag/Tag";
import type {ComplianceRequirementGridRow} from "~/pages/supplier-360/types/Supplier360Types";
import {
    COMPLIANCE_LAYERS,
    COMPLIANCE_REMINDER_STATES,
    COMPLIANCE_REQUIREMENT_STATES,
    QUALIFICATION_STATUSES,
} from "~/pages/supplier-360/types/Supplier360Types";
import {
    LAYER_LABEL,
    QUALIFICATION_COLOR,
    QUALIFICATION_LABEL,
} from "~/pages/supplier-360/tabs/SupplierCompliance/constants/complianceLabels";
import {EMPTY_CELL, formatDate} from "~/components/data-grid/cells/gridCellFormat";
import {centered, daysLeftLine, emptyCell, subLine} from "~/components/data-grid/cells/gridCells";
import {STATUS_CHIP} from "~/components/status-chip/statusChips";
import {REMINDER_CHIP} from "~/pages/supplier-360/tabs/SupplierCompliance/components/complianceReminderChips";
import {VERIFICATION_CHIP} from "~/pages/supplier-360/tabs/SupplierCompliance/components/complianceVerificationChips";
import {requalLine} from "~/pages/supplier-360/tabs/SupplierCompliance/components/complianceCells";

/** A records-grid row — the requirement plus its `bu_qualification` fields from `related_data`. */
export type ComplianceRequirementRow = ComplianceRequirementGridRow;

type ComplianceRequirementColumnOptions = {
    onEdit: (row: ComplianceRequirementRow) => void;
    onOpen: (row: ComplianceRequirementRow) => void;
};

/** Company-wide rows have no qualification of their own — the design shows them as inherited. */
const INHERITED_LABEL = 'Inherited';

function layerLabel(row: ComplianceRequirementRow): string {
    return LAYER_LABEL[row.layer];
}

export function getComplianceRequirementColumns({
                                                    onEdit,
                                                    onOpen
                                                }: ComplianceRequirementColumnOptions): GridColDef<ComplianceRequirementRow>[] {
    return [
        {
            field: 'name',
            headerName: 'Compliance record',
            flex: 1.6,
            minWidth: 240,
            renderCell: ({row}) => centered(
                <Stack gap={0.5}>
                    <Typography variant="body2" noWrap>{row.name}</Typography>
                    {subLine(row.kind)}
                </Stack>,
            ),
        },
        {
            field: 'layer',
            headerName: 'Layer',
            type: 'singleSelect',
            valueOptions: COMPLIANCE_LAYERS.map(value => ({value, label: LAYER_LABEL[value]})),
            flex: 1,
            minWidth: 150,
            valueGetter: (_value, row) => row.layer,
            renderCell: ({row}) => (
                <Stack gap={0.5}>
                    <Tag
                        size="small"
                        variant="outlined"
                        color={row.layer === 'company_wide' ? 'default' : 'secondary'}
                        icon={row.layer === 'company_wide'
                            ? <BuildingIcon width={14} height={14}/>
                            : <StoreIcon width={14} height={14}/>}
                        label={(row.layer === 'business_unit' && row.bu_name) ? row.bu_name : layerLabel(row)}
                    />
                </Stack>
            ),
        },
        {
            /** `bu_qualification.region` from `related_data` — filters / sorts as `bu_qualification__region`. */
            field: 'region',
            headerName: 'Applies to',
            flex: 1.1,
            minWidth: 170,
            /** Company-wide rows are empty: the API doesn't return the supplier's BU count. */
            valueGetter: (_value, row) => row.layer === 'company_wide' ? EMPTY_CELL : row.region ?? EMPTY_CELL,
            renderCell: ({row}) => row.layer === 'company_wide'
                ? emptyCell()
                : centered(
                    <>
                        <Typography variant="body2" noWrap>{row.region ?? EMPTY_CELL}</Typography>
                        {subLine(row.category ?? EMPTY_CELL)}
                    </>,
                ),
        },
        {
            /** `bu_qualification.qualification_status` from `related_data`; company-wide rows are inherited. */
            field: 'qualification_status',
            headerName: 'Qualification',
            type: 'singleSelect',
            valueOptions: QUALIFICATION_STATUSES.map(value => ({value, label: QUALIFICATION_LABEL[value]})),
            flex: 1,
            minWidth: 160,
            renderCell: ({row}) => {
                if (row.layer === 'company_wide') {
                    return centered(<Chip size="small" color="success" label={INHERITED_LABEL}/>);
                }
                if (!row.qualification_status) return emptyCell();
                return centered(
                    <>
                        <Chip
                            size="small"
                            color={QUALIFICATION_COLOR[row.qualification_status]}
                            label={QUALIFICATION_LABEL[row.qualification_status]}
                        />
                        {requalLine(row.requalification_due ?? null)}
                    </>,
                );
            },
        },
        {
            field: 'expires_at',
            headerName: 'Valid until',
            type: 'date',
            flex: 0.9,
            minWidth: 140,
            valueGetter: (_value, row) => row.expires_at ? new Date(row.expires_at) : null,
            renderCell: ({row}) => centered(
                <>
                    <Typography variant="body2">{formatDate(row.expires_at)}</Typography>
                    {daysLeftLine(row.expires_at)}
                </>,
            ),
        },
        {
            /**
             * The latest evidence's `verification_status` (§2.8) — `null` until evidence is uploaded.
             * Not filterable: it's an annotation, which the MUIX filter can't resolve to a model field.
             */
            field: 'verification_status',
            headerName: 'Verification',
            flex: 0.9,
            minWidth: 140,
            filterable: false,
            renderCell: ({row}) => row.verification_status ? centered(VERIFICATION_CHIP[row.verification_status]) : emptyCell(),
        },
        {
            field: 'state',
            headerName: 'State',
            type: 'singleSelect',
            valueOptions: COMPLIANCE_REQUIREMENT_STATES.map(value => ({
                value,
                label: String(STATUS_CHIP[value].props.label)
            })),
            flex: 0.8,
            minWidth: 120,
            renderCell: ({row}) => centered(STATUS_CHIP[row.state]),
        },
        {
            field: 'last_reminder_state',
            headerName: 'Reminder',
            type: 'singleSelect',
            valueOptions: COMPLIANCE_REMINDER_STATES.map(value => ({
                value,
                label: String(REMINDER_CHIP[value].props.label)
            })),
            flex: 1.2,
            minWidth: 190,
            renderCell: ({row}) => centered(REMINDER_CHIP[row.last_reminder_state]),
        },
        {
            field: 'actions',
            headerName: '',
            width: 130,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            disableExport: true,
            renderCell: ({row}) => (
                // Keep the buttons' clicks from also reaching the row's own click.
                <Stack direction="row" gap={0.5} sx={{alignItems: 'center', height: '100%'}}
                       onClick={event => event.stopPropagation()}>
                    <Button size="small" onClick={() => onOpen(row)}
                            sx={{textTransform: 'none', minWidth: 0}}>Details</Button>
                    <IconButton size="small" title="Edit record" onClick={() => onEdit(row)}>
                        <EditIcon fontSize="small"/>
                    </IconButton>
                </Stack>
            ),
        },
    ];
}
